import Accordion from "@/common/components/Accordion";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import SmallBadge from "@/common/components/SmallBadge";
import { cn } from "@/common/utils/cn";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { format } from "date-fns";
import { CalendarCheck2Icon, ClockIcon, CrownIcon, WrenchIcon } from "lucide-react";
import { useMemo } from "react";
import { Tooltip } from "react-tooltip";
import { MaintenanceEstablishmentChoiceAuctionEntryData } from "../../../../types/auctionEntry.type";
import { MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData } from "../../../../types/updatedSummary.type";
import AuctionProposalDetails from "../AuctionProposal/AuctionProposalDetails";
import { auctionProposalLeadValueByDirectiveEntryKlass } from "../../constants/auctionProposalLeadValue.constant";

interface AuctionProposalProps {
  proposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData;
  yourProposal: MaintenanceEstablishmentChoiceAuctionEntryUpdatedSummaryProposalData | null | undefined;
  auctionEntry: MaintenanceEstablishmentChoiceAuctionEntryData | undefined;
  auctionEntryUpdatedAt: number;
  auctionKind?: string;
  directiveEntryType?: MaintenanceForwardingsDirectiveEntryKlass;
}

const AuctionProposal = ({
  proposal,
  yourProposal,
  auctionEntry,
  auctionEntryUpdatedAt,
  auctionKind,
  directiveEntryType,
}: AuctionProposalProps) => {
  const isYourProposal = yourProposal && proposal.position === yourProposal?.position;
  const isFirstPosition = proposal.position === 1;
  const LeadValue = directiveEntryType ? auctionProposalLeadValueByDirectiveEntryKlass[directiveEntryType] : undefined;

  const proposalToDisplay = useMemo(() => {
    if (isYourProposal)
      return yourProposal;

    return proposal;
  }, [isYourProposal]);

  const canExpand = proposalToDisplay.id && Number(auctionEntry?.forwarding?.directive_entry?.objectives?.length) > 0;

  const tooltipId = `auction-proposal-badge-${proposalToDisplay.id}`;

  if (auctionKind === 'closed' && !proposalToDisplay.id) {
    return (
      <div className="bg-white-100 h-12 rounded-lg overflow-hidden" style={{ backdropFilter: 'blur(4px)' }}>
        <div
          className="bg-gradient-to-r from-transparent via-white-100 to-transparent opacity-15 h-12"
          style={{ animation: 'shimmer 5s infinite linear' }}
        />
      </div>
    );
  }

  return (
    <Accordion
      className={cn('p-0 rounded-lg', isYourProposal && 'border-l-[3px] border-blue-800')}
      triggerClassName="p-0"
      bodyClassName="p-0"
      contentClassName="p-0 pb-1"
      iconStyles={cn('mr-2 -ml-1', !canExpand && 'hidden')}
      triggerDisabled={!canExpand}
      head={(
        <div
          className="px-2.5 py-3 w-full rounded-lg flex items-center justify-between gap-2 min-h-12">
          <div className="flex items-center gap-1.5">
            <div className="flex items-center gap-1">
              {isFirstPosition && (
                <CrownIcon
                  size={16}
                  strokeWidth={1.5}
                  className="text-warning-secondary"
                />
              )}

              <p className={cn('text-sm font-medium leading-none', isFirstPosition && 'text-yellow-400')}>
                {proposalToDisplay.position}º
              </p>
            </div>

            {isYourProposal ? (
              <>
                <span className="text-xs leading-none">
                  |
                </span>

                <span className="text-xs leading-none">
                  Sua proposta
                </span>
              </>
            ) : (
              <ImageWithDisplayName
                placeholderIcon={WrenchIcon}
                displayName={proposalToDisplay.establishment_fantasy_name}
                displayNameClassName="text-sm"
                imageClassName="size-5"
                containerClassName="gap-1.5"
                iconClassName="size-3.5"
              />
            )}
          </div>

          <div className="flex items-center gap-1">
            {LeadValue && (
              <LeadValue
                proposal={proposalToDisplay}
                tooltipId={tooltipId}
              />
            )}

            {proposalToDisplay.contents?.deadline_to_complete_in_days != null && (
              <SmallBadge
                data-tooltip-id={tooltipId}
                data-tooltip-content="Prazo para conclusão"
                LeftIcon={ClockIcon}
                text={`${proposalToDisplay.contents?.deadline_to_complete_in_days} Dias`}
                className="bg-white-200"
              />
            )}

            {proposalToDisplay.contents?.start_at && (
              <SmallBadge
                data-tooltip-id={tooltipId}
                data-tooltip-content="Data de início"
                LeftIcon={CalendarCheck2Icon}
                text={format(new Date(proposalToDisplay.contents?.start_at), 'dd/MM HH:mm')}
                className="bg-white-200"
              />
            )}

            <Tooltip id={tooltipId} />
          </div>
        </div>
      )}
      body={
        canExpand
          ? <AuctionProposalDetails
            proposal={proposalToDisplay}
            auctionEntry={auctionEntry}
            auctionEntryUpdatedAt={auctionEntryUpdatedAt}
          />
          : <></>
      }
    />
  );
}

export default AuctionProposal;
